import {
    CircularProgress,
    Container,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';
import AdminHeader from '../../../components/atom/AdminHeader';
import ActionIconButton from '../../../components/atom/ActionIconButton';
import FormSection from '../../../components/atom/FormSection';
import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import { CheckIcon, DeleteIcon, EditIcon, TimesIcon } from '../../../icons';
import LocationCreateOrEditForm from '../locationedit/components/LocationCreateOrEditForm';

export default function LocationsPage() {
    const { notifySuccess } = useNotify();
    const [isAddingNewLocation, setIsAddingNewLocation] = useState(false);
    const [refreshLocations, setRefreshLocations] = useState(false);
    const [stripeError, setStripeError] = useState(false);
    const [locations, loading, locationsError] = useSwaggerClient(
        'terminals',
        'getTerminalLocations',
        {},
        [refreshLocations]
    );

    const addLocation = (values, { setSubmitting }) => {
        doSwaggerCall('terminals', 'createTerminalLocation', {
            locationData: {
                display_name: values.display_name,
                address: {
                    country: values.country,
                    postal_code: values.postal_code,
                    state: values.state,
                    city: values.city,
                    line1: values.line1,
                    line2: values.line2
                }
            }
        })
            .then(() => {
                notifySuccess('Successfully created location');
                setStripeError(false);
            })
            .catch(err => {
                setStripeError(`${err.code} - ${err.message}`);
            })
            .finally(() => {
                setSubmitting(false);
                setIsAddingNewLocation(false);
                setRefreshLocations(!refreshLocations);
            });
    };

    const deleteLocation = id => {
        doSwaggerCall('terminals', 'deleteTerminalLocation', {
            locationId: id
        })
            .then(() => {
                notifySuccess('Successfully deleted location');
                setStripeError(false);
            })
            .catch(err => {
                setStripeError(`${err.code} - ${err.message}`);
            })
            .finally(() => {
                setRefreshLocations(!refreshLocations);
            });
    };

    if (locationsError) {
        return <Alert severity="error">Could not load terminal locations.</Alert>;
    }

    if (stripeError) {
        return <Alert severity="error">{stripeError}</Alert>;
    }

    if (loading) {
        return (
            <Grid container justifyContent="center" className="mt-2">
                <CircularProgress />
            </Grid>
        );
    }

    if (isAddingNewLocation) {
        return (
            <Container fixed className="activity-edit page">
                <Grid container item xs={12} spacing={2}>
                    <AdminHeader title="Terminal locations" />
                    <LocationCreateOrEditForm
                        isCreate
                        onBack={() => setIsAddingNewLocation(false)}
                        onSubmit={addLocation}
                    />
                </Grid>
            </Container>
        );
    }
    return (
        <Grid container className="bg-white client-table page" alignItems="center" spacing={2}>
            <AdminHeader
                title="Terminal locations"
                createLabel="New location"
                onCreate={() => setIsAddingNewLocation(true)}
            />
            <Grid container item xs={12} spacing={2}>
                <FormSection title="Locations">
                    <Grid item xs={12}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Id</TableCell>
                                        <TableCell>Country</TableCell>
                                        <TableCell>Address</TableCell>
                                        <TableCell>Display name</TableCell>
                                        <TableCell>Live mode</TableCell>
                                        <TableCell style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {locations?.map(location => (
                                        <TableRow key={location.id}>
                                            <TableCell>{location.id}</TableCell>
                                            <TableCell>{location.address.country}</TableCell>
                                            <TableCell>{`${location.address.postal_code}, ${location.address.state} ${location.address.city} ${location.address.line1} ${location.address.line2}`}</TableCell>
                                            <TableCell>{location.display_name}</TableCell>
                                            <TableCell>
                                                {location.livemode ? (
                                                    <Tooltip title="In live mode">
                                                        <CheckIcon color="success" />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="In test mode">
                                                        <TimesIcon color="error" />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <ActionIconButton
                                                    title="Edit"
                                                    icon={EditIcon}
                                                    to={`/admin/terminallocations/${location.id}`}
                                                    color="primary"
                                                />
                                                <ActionIconButton
                                                    title="Delete"
                                                    icon={DeleteIcon}
                                                    onClick={() => deleteLocation(location.id)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </FormSection>
            </Grid>
        </Grid>
    );
}
